import { authClient } from "@/api/auth-client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/shared/components/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { type LoginInput, loginSchema } from "@template/shared";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { PasswordInput } from "./password-input";

/** Login form with email/password fields. Uses React Hook Form + Zod validation + Better Auth client. */
export function LoginForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    setServerError(null);
    const result = await authClient.signIn.email({
      email: data.email,
      password: data.password,
    });
    if (result.error) {
      setServerError(result.error.message ?? "Invalid email or password");
      setIsLoading(false);
      return;
    }
    navigate({ to: "/dashboard" });
  });

  return (
    <Card className="w-full max-w-md" data-testid="auth-login-card">
      <CardHeader className="text-center">
        <CardTitle>Sign In</CardTitle>
        <CardDescription>Enter your credentials to access your account</CardDescription>
      </CardHeader>
      <CardContent>
        <FormError message={serverError ?? undefined} testId="auth-login-error" />

        <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-login-form">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoFocus
              aria-describedby={errors.email ? "email-error" : undefined}
              data-testid="auth-login-email-input"
              {...register("email")}
            />
            {errors.email && (
              <p
                id="email-error"
                className="text-sm text-destructive"
                data-testid="auth-login-email-error"
              >
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/zapomenute-heslo"
                className="text-xs text-muted-foreground hover:underline"
                data-testid="auth-login-forgot-link"
              >
                Zapomněli jste heslo?
              </Link>
            </div>
            <PasswordInput
              id="password"
              aria-describedby={errors.password ? "password-error" : undefined}
              data-testid="auth-login-password-input"
              {...register("password")}
            />
            {errors.password && (
              <p
                id="password-error"
                className="text-sm text-destructive"
                data-testid="auth-login-password-error"
              >
                {errors.password.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            data-testid="auth-login-submit-button"
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground" data-testid="auth-login-invite-note">
          Přístup do aplikace vám založí administrátor.
        </p>
      </CardFooter>
    </Card>
  );
}
